import { z } from 'zod';
export const QUICK_PICK_FIELDS = [
    'chiefComplaint',
    'presentingComplaints',
    'historyOfPresentIllness',
    'pastHistory',
    'surgicalHistory',
    'familyHistory',
    'socialHistory',
    'drugHistory',
    'reviewOfSystems',
    'examination',
    'treatmentPlan',
    'advice',
];
export const QUICK_PICK_SHOWN = 12;
export const QUICK_PICK_KEPT = 30;
export const QUICK_PICK_MIN_USES = 2;
export const QUICK_PICK_PER_NOTE = 15;
export const QUICK_PICK_PHRASE_MIN = 3;
export const QUICK_PICK_PHRASE_MAX = 80;
export const QUICK_PICK_MAX_WORDS = 10;
export function isQuickPickField(value) {
    return QUICK_PICK_FIELDS.includes(value);
}
const PIECE_BREAK = /\r?\n|[;؛]|[.,،](?=\s|$)/;
const LEADING_MARK = /^[\s\-–—•*·>]+/;
const TRAILING_MARK = /[\s.,;:!?،؛]+$/;
const LETTER = /\p{L}/u;
function phraseKey(phrase) {
    return phrase.toLocaleLowerCase();
}
export function cleanPhrase(raw) {
    const phrase = raw.replace(LEADING_MARK, '').replace(TRAILING_MARK, '').replace(/\s+/g, ' ').trim();
    if (phrase.length < QUICK_PICK_PHRASE_MIN || phrase.length > QUICK_PICK_PHRASE_MAX)
        return null;
    if (!LETTER.test(phrase))
        return null;
    if (phrase.split(' ').length > QUICK_PICK_MAX_WORDS)
        return null;
    return phrase;
}
export function phrasesOf(text) {
    if (!text)
        return [];
    const seen = new Set();
    const out = [];
    for (const piece of text.split(PIECE_BREAK)) {
        const phrase = cleanPhrase(piece);
        if (!phrase || seen.has(phraseKey(phrase)))
            continue;
        seen.add(phraseKey(phrase));
        out.push(phrase);
        if (out.length >= QUICK_PICK_PER_NOTE)
            break;
    }
    return out;
}
function ranked(entries) {
    return [...entries].sort((a, b) => b.n - a.n || b.at - a.at);
}
export function learnPhrases(store, texts, now) {
    const next = { ...store };
    for (const field of QUICK_PICK_FIELDS) {
        const phrases = phrasesOf(texts[field]);
        if (phrases.length === 0)
            continue;
        const entries = new Map((next[field] ?? []).map((entry) => [phraseKey(entry.t), { ...entry }]));
        for (const phrase of phrases) {
            const known = entries.get(phraseKey(phrase));
            if (known) {
                known.n += 1;
                known.at = now;
            }
            else {
                entries.set(phraseKey(phrase), { t: phrase, n: 1, at: now });
            }
        }
        next[field] = ranked([...entries.values()]).slice(0, QUICK_PICK_KEPT);
    }
    return next;
}
export function learnedPicks(store, field, limit = QUICK_PICK_SHOWN) {
    return ranked((store[field] ?? []).filter((entry) => entry.n >= QUICK_PICK_MIN_USES))
        .slice(0, limit)
        .map((entry) => entry.t);
}
export function quickPicksOf(store) {
    const phrases = {};
    for (const field of QUICK_PICK_FIELDS) {
        const picks = learnedPicks(store, field);
        if (picks.length > 0)
            phrases[field] = picks;
    }
    return { phrases };
}
const learnedPhraseSchema = z.object({
    t: z.string().min(1).max(QUICK_PICK_PHRASE_MAX),
    n: z.number().int().min(1),
    at: z.number().int().min(0),
});
export function readPhraseStore(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return {};
    const store = {};
    for (const field of QUICK_PICK_FIELDS) {
        const parsed = z.array(learnedPhraseSchema).safeParse(value[field]);
        if (parsed.success && parsed.data.length > 0)
            store[field] = ranked(parsed.data).slice(0, QUICK_PICK_KEPT);
    }
    return store;
}
export function appendPhrase(current, phrase, joiner = '. ') {
    const text = current ?? '';
    if (text.trim() === '')
        return phrase;
    const existing = text.split(PIECE_BREAK).map((piece) => phraseKey(piece.replace(LEADING_MARK, '').replace(TRAILING_MARK, '').trim()));
    if (existing.includes(phraseKey(phrase)))
        return text;
    if (/\n\s*$/.test(text))
        return `${text}${phrase}`;
    const trimmed = text.replace(/\s+$/, '');
    if (/[.,;:،؛]$/.test(trimmed))
        return `${trimmed} ${phrase}`;
    return `${trimmed}${joiner}${phrase}`;
}
export const FAVOURITE_DIAGNOSES_MAX = 50;
export const DIAGNOSIS_PICKS_SHOWN = 15;
export const favouriteDiagnosisSchema = z.object({
    icdCode: z.string().trim().min(1).max(20),
});
export const diagnosisPicksQuerySchema = z.object({
    departmentId: z.string().uuid().optional(),
});
const diagnosisPickSchema = z.object({ code: z.string().min(1).max(20), title: z.string().min(1).max(400) });
export function readFavourites(value) {
    const parsed = z.array(diagnosisPickSchema).safeParse(value);
    return parsed.success ? distinctPicks(parsed.data, FAVOURITE_DIAGNOSES_MAX) : [];
}
export function withFavourite(list, pick) {
    if (list.some((entry) => entry.code === pick.code))
        return [...list];
    if (list.length >= FAVOURITE_DIAGNOSES_MAX)
        return null;
    return [pick, ...list];
}
export function withoutFavourite(list, code) {
    return list.filter((entry) => entry.code !== code);
}
export function distinctPicks(rows, limit = DIAGNOSIS_PICKS_SHOWN) {
    const seen = new Set();
    const out = [];
    for (const row of rows) {
        if (seen.has(row.code))
            continue;
        seen.add(row.code);
        out.push(row);
        if (out.length >= limit)
            break;
    }
    return out;
}
export const DEPARTMENT_PHRASES_PER_FIELD = 20;
export const DEPARTMENT_PHRASE_MAX = 200;
export const DEPARTMENT_PHRASES_UNKNOWN_FIELD = 'That note field does not take quick-pick phrases';
export const saveDepartmentPhrasesSchema = z.object({
    phrases: z
        .record(z.string(), z.array(z.string().trim().min(1).max(DEPARTMENT_PHRASE_MAX)).max(DEPARTMENT_PHRASES_PER_FIELD))
        .refine((value) => Object.keys(value).every(isQuickPickField), { message: DEPARTMENT_PHRASES_UNKNOWN_FIELD }),
});
export const quickPicksQuerySchema = z.object({
    departmentId: z.string().uuid().optional(),
});
export function cleanDepartmentPhrase(raw) {
    const phrase = raw.replace(/\s+/g, ' ').trim();
    if (phrase.length === 0 || phrase.length > DEPARTMENT_PHRASE_MAX)
        return null;
    return phrase;
}
export function cleanDepartmentPhrases(input) {
    const out = {};
    for (const field of QUICK_PICK_FIELDS) {
        const seen = new Set();
        const list = [];
        for (const raw of input[field] ?? []) {
            const phrase = cleanDepartmentPhrase(raw);
            if (!phrase || seen.has(phraseKey(phrase)))
                continue;
            seen.add(phraseKey(phrase));
            list.push(phrase);
            if (list.length >= DEPARTMENT_PHRASES_PER_FIELD)
                break;
        }
        if (list.length > 0)
            out[field] = list;
    }
    return out;
}
export function readDepartmentPhrases(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return {};
    const raw = {};
    for (const field of QUICK_PICK_FIELDS) {
        const list = value[field];
        if (Array.isArray(list))
            raw[field] = list.filter((item) => typeof item === 'string');
    }
    return cleanDepartmentPhrases(raw);
}
export function phraseCount(lists) {
    return QUICK_PICK_FIELDS.reduce((sum, field) => sum + (lists[field]?.length ?? 0), 0);
}
export function mergeQuickPicks(department, learned, defaults) {
    const seen = new Set();
    const out = [];
    const push = (text, source) => {
        const key = phraseKey(text.trim());
        if (key === '' || seen.has(key))
            return;
        seen.add(key);
        out.push({ text, source });
    };
    for (const text of department ?? [])
        push(text, 'department');
    for (const text of learned ?? [])
        push(text, 'learned');
    for (const text of defaults)
        push(text, 'default');
    return out;
}
//# sourceMappingURL=quick-picks.js.map